import { Entity } from './Entity';
import { System } from './System';
import { Logger } from '../../utils/Logger';

/**
 * Core SystemManager class that manages all systems in the ECS architecture.
 * Handles system registration, entity management, and system updates.
 * This is the base implementation that can be extended for specific game needs.
 */
export class SystemManager {
  private systems: System[] = [];
  private logger: Logger;
  private paused: boolean = false;

  constructor() {
    this.logger = Logger.getInstance();
  }

  /**
   * Add a system to the manager
   */
  public addSystem(system: System): void {
    this.systems.push(system);
    this.logger.info(`Added system: ${system.constructor.name}`);
  }

  /**
   * Remove a system from the manager
   */
  public removeSystem(system: System): void {
    const index = this.systems.indexOf(system);
    if (index !== -1) {
      this.systems.splice(index, 1);
      this.logger.info(`Removed system: ${system.constructor.name}`);
    }
  }

  /**
   * Get a system by its constructor
   */
  public getSystem<T extends System>(
    systemType: new (...args: any[]) => T
  ): T | undefined {
    return this.systems.find((system) => system instanceof systemType) as
      | T
      | undefined;
  }

  /**
   * Get all systems (for debug purposes)
   */
  public getSystems(): System[] {
    return [...this.systems]; // Return a copy to prevent external modification
  }

  /**
   * Add an entity to all systems that can handle it
   */
  public addEntity(entity: Entity): void {
    this.logger.info(`Adding entity '${entity.getId()}' to systems`);
    for (const system of this.systems) {
      if (system.canProcessEntity(entity)) {
        system.addEntity(entity);
        this.logger.debug(`Added to system: ${system.constructor.name}`);
      }
    }
  }

  /**
   * Remove an entity from all systems
   */
  public removeEntity(entity: Entity): void {
    this.logger.info(`Removing entity '${entity.getId()}' from systems`);
    for (const system of this.systems) {
      system.removeEntity(entity);
      this.logger.debug(`Removed from system: ${system.constructor.name}`);
    }
  }

  /**
   * Update all systems
   */
  public update(deltaTime: number): void {
    const effectiveDeltaTime = this.paused ? 0 : deltaTime;
    for (const system of this.systems) {
      system.update(effectiveDeltaTime);
    }
  }

  /**
   * Pause all systems (they will still update but with deltaTime = 0)
   */
  public pause(): void {
    this.paused = true;
    this.logger.info('Systems paused');
  }

  /**
   * Resume all systems
   */
  public resume(): void {
    this.paused = false;
    this.logger.info('Systems resumed');
  }

  /**
   * Clear all systems and entities
   */
  public clear(): void {
    for (const system of this.systems) {
      system.removeAllEntities();
    }
    this.systems = [];
    this.paused = false;
    this.logger.info('Cleared all systems');
  }
}
